import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, ChevronRight, Settings } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { RuleConditionBuilder } from "./RuleConditionBuilder";
import { CategoryRule, TagCondition, SimpleTag } from "../types/categoryRules";

interface ConvexCategory {
  _id: string;
  name: string;
  description?: string;
  icon: string;
  tags: string[]; // Simple string array
}

interface CategoryTableRowProps {
  category: ConvexCategory;
  rule?: CategoryRule;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onUpdateRule: (categoryId: string, updates: Partial<CategoryRule>) => void;
  onAddCondition: (categoryId: string) => void;
  onUpdateCondition: (categoryId: string, conditionId: string, updates: Partial<TagCondition>) => void;
  onRemoveCondition: (categoryId: string, conditionId: string) => void;
  hasValidationError?: boolean;
  isEditMode?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'disabled', label: 'Not Allowed' },
  { value: 'fully_enabled', label: (categoryName: string) => `All ${categoryName} apps allowed` },
  { value: 'enabled_with_rules', label: (categoryName: string) => `Certain ${categoryName} apps allowed…` },
];

const getStatusDisplayText = (status: string, categoryName: string) => {
  switch (status) {
    case 'disabled':
      return 'Not Allowed';
    case 'fully_enabled':
      return `All ${categoryName} apps allowed`;
    case 'enabled_with_rules':
      return `Certain ${categoryName} apps allowed…`;
    default:
      return 'Not Allowed';
  }
};

// Helper function to convert string array to SimpleTag array with proper error handling
const convertTagsToSimpleTags = (tagNames: string[] | undefined | null): SimpleTag[] => {
  console.log('[convertTagsToSimpleTags] Input:', tagNames, 'Type:', typeof tagNames, 'Is Array:', Array.isArray(tagNames));
  
  // Handle cases where tagNames is not a valid array
  if (!tagNames || !Array.isArray(tagNames)) {
    console.log('[convertTagsToSimpleTags] Invalid input, returning empty array');
    return [];
  }

  const result = tagNames.map(name => ({
    id: name, // Use the tag name as the ID for simplicity
    name: name,
    color: '#6B7280' // Default color
  }));
  
  console.log('[convertTagsToSimpleTags] Result:', result);
  return result;
};

export const CategoryTableRow = ({
  category,
  rule,
  isExpanded,
  onToggleExpanded,
  onUpdateRule,
  onAddCondition,
  onUpdateCondition,
  onRemoveCondition,
  hasValidationError,
  isEditMode = false
}: CategoryTableRowProps) => {
  console.log('[CategoryTableRow] Rendering category:', category.name, 'with ID:', category._id, 'and tags:', category.tags);
  
  // Validate category ID
  const categoryId = category._id;
  if (!categoryId || typeof categoryId !== 'string') {
    console.error('[CategoryTableRow] Invalid category ID:', categoryId, 'for category:', category.name);
    return null; // Don't render if ID is invalid
  }
  
  const getIcon = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName] || LucideIcons.Folder;
    return IconComponent;
  };

  const Icon = getIcon(category.icon);

  const handleStatusChange = (value: string) => {
    console.log('[handleStatusChange] Changing status for category ID:', categoryId, 'to:', value);
    
    const updates: Partial<CategoryRule> = {
      status: value as CategoryRule['status'],
      conditions: value === 'enabled_with_rules' ? (rule?.conditions?.length ? rule.conditions : []) : []
    };

    onUpdateRule(categoryId, updates);

    if (value === 'enabled_with_rules' && !isExpanded) {
      onToggleExpanded();
    }
  };

  const renderRuleBuilder = () => {
    if (!rule || rule.status !== 'enabled_with_rules' || !isExpanded) return null;

    // Convert string array to SimpleTag array for the RuleConditionBuilder with error handling
    const simpleTags = convertTagsToSimpleTags(category.tags);
    console.log('[renderRuleBuilder] Converted tags for category', category.name, ':', simpleTags);

    return (
      <TableRow>
        <TableCell colSpan={3} className="bg-gray-50">
          <div className={`p-4 space-y-3 ${hasValidationError ? 'border-2 border-red-500 rounded-lg' : ''}`}>
            <div className="flex items-center justify-between">
              <h5 className="text-sm font-medium text-gray-700">Custom Rules for {category.name}</h5>
              {hasValidationError && (
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Please fix this category's rules in order to save</span>
                </div>
              )}
            </div>

            <RuleConditionBuilder
              categoryId={categoryId}
              tags={simpleTags}
              conditions={rule.conditions || []}
              onAddCondition={onAddCondition}
              onUpdateCondition={onUpdateCondition}
              onRemoveCondition={onRemoveCondition}
            />
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const getRowClassName = () => {
    const status = rule?.status || 'disabled';
    let baseClass = "transition-all duration-200 h-16";
    
    if (status === 'disabled') {
      baseClass += " bg-red-50 hover:bg-red-100";
    } else if (status === 'fully_enabled') {
      baseClass += " bg-green-100 hover:bg-green-150";
    } else if (status === 'enabled_with_rules') {
      baseClass += " bg-green-100 hover:bg-green-150 cursor-pointer";
    } else {
      baseClass += " hover:bg-gray-50";
    }
    
    return baseClass;
  };

  const getCellClassName = (status: string) => {
    return "py-4";
  };

  const getTextClassName = (status: string) => {
    return status === 'disabled' ? 'text-gray-600' : 'text-gray-900';
  };

  const getIconClassName = (status: string) => {
    return status === 'disabled' ? 'text-gray-500' : 'text-gray-600';
  };

  const status = rule?.status || 'disabled';

  // Get tag count safely
  const tagCount = Array.isArray(category.tags) ? category.tags.length : 0;

  return (
    <>
      <TableRow 
        className={getRowClassName()}
        onClick={status === 'enabled_with_rules' ? onToggleExpanded : undefined}
      >
        <TableCell className={getCellClassName(status)}>
          <div className="flex items-center gap-3">
            <div>
              <div className={`font-medium ${getTextClassName(status)}`}>
                {category.name}
              </div>
              {category.description && (
                <div className="text-sm text-gray-500">{category.description}</div>
              )}
            </div>
            {status === 'enabled_with_rules' && (
              <div className="ml-auto flex items-center gap-2 text-yellow-600">
                <Settings className="w-4 h-4" />
                <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </div>
            )}
          </div>
        </TableCell>
        <TableCell className={getCellClassName(status)}>
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <div onClick={(e) => e.stopPropagation()}>
                <Select
                  value={rule?.status || 'disabled'}
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className={`h-8 text-sm min-w-[200px] ${hasValidationError ? 'border-red-500' : ''}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        disabled={option.value === 'enabled_with_rules'}
                        className={option.value === 'enabled_with_rules' ? 'text-gray-400 cursor-not-allowed opacity-50' : ''}
                      >
                        {typeof option.label === 'function' ? option.label(category.name) : option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="text-sm font-medium">
                {getStatusDisplayText(status, category.name)}
              </div>
            )}
          </div>
        </TableCell>
      </TableRow>
      {renderRuleBuilder()}
    </>
  );
};
